import React, { useState, useEffect, useRef, useContext } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faGlobe,
  faUser,
  faTimes,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const ShortcutsSettingsSection = () => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token, nombreCompleto, idUsuario } = useToken();
  const { showSuccessToast, showErrorToast } = useToast();

  const [shortcuts, setShortcuts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all' | 'global' | 'personal'
  const isInitialMount = useRef(true);

  // Modal Crear/Editar State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formCommand, setFormCommand] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formText, setFormText] = useState('');
  const [formIsGlobal, setFormIsGlobal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Modal Eliminar State
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Cargar catálogo de atajos desde la base de datos
  const fetchShortcuts = async (searchQuery = searchTerm) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (idUsuario) params.append('idUsuario', idUsuario);
      if (searchQuery && searchQuery.trim()) params.append('searchTerm', searchQuery.trim());

      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`/api/SupportEngine/GetAtajos${query}`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        }
      });

      if (response.ok) {
        const result = await response.json();
        const list = result.data || result;
        if (Array.isArray(list)) {
          setShortcuts(list);
        }
      } else {
        showErrorToast('Error al cargar la lista de atajos.');
      }
    } catch (err) {
      console.error('Error al cargar atajos:', err);
      showErrorToast('Error de conexión al cargar atajos.');
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar datos al montar y con debounce al buscar
  useEffect(() => {
    if (!token) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchShortcuts(searchTerm);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(() => {
      fetchShortcuts(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, token, idUsuario]);

  // Abrir modal para crear
  const handleOpenCreate = () => {
    setEditingId(null);
    setFormCommand('');
    setFormTitle('');
    setFormText('');
    setFormIsGlobal(false);
    setIsFormOpen(true);
  };

  // Abrir modal para editar
  const handleOpenEdit = atajo => {
    setEditingId(atajo.idAtajo);
    setFormCommand((atajo.vComando || atajo.label || '').replace(/^\//, ''));
    setFormTitle(atajo.vTitulo || atajo.title || '');
    setFormText(atajo.vTexto || atajo.text || '');
    setFormIsGlobal(!atajo.idUsuario);
    setIsFormOpen(true);
  };

  // Guardar (Crear o Actualizar)
  const handleSave = async e => {
    e.preventDefault();
    const cleanCmd = formCommand.trim().toLowerCase().replace(/^\//, '');
    const cleanTitle = formTitle.trim();
    const cleanText = formText.trim();

    if (!cleanCmd) {
      showErrorToast('El comando es obligatorio (ej: saludo).');
      return;
    }
    if (!cleanText) {
      showErrorToast('El texto de la respuesta rápida es obligatorio.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        idAtajo: editingId || null,
        idUsuario: formIsGlobal ? null : (parseInt(idUsuario) || null),
        vComando: `/${cleanCmd}`,
        vTitulo: cleanTitle || `/${cleanCmd}`,
        vTexto: cleanText,
        vCreatedUser: nombreCompleto || 'Agente'
      };

      const response = await fetch('/api/SupportEngine/SaveAtajo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        let msg = result.message || 'Error al guardar atajo';
        if (result.details) {
          try {
            const parsed = typeof result.details === 'string' ? JSON.parse(result.details) : result.details;
            if (parsed.message) msg = parsed.message;
          } catch (_) {
            msg = result.details;
          }
        }
        throw new Error(msg);
      }

      showSuccessToast(editingId ? 'Atajo actualizado con éxito.' : 'Atajo creado con éxito.');
      setIsFormOpen(false);
      await fetchShortcuts();
    } catch (err) {
      showErrorToast(err.message || 'Error al guardar el atajo.');
    } finally {
      setIsSaving(false);
    }
  };

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch('/api/SupportEngine/DeleteAtajo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          idAtajo: itemToDelete.idAtajo,
          idUsuario: parseInt(idUsuario) || null,
          vUpdatedUser: nombreCompleto || 'Agente'
        })
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        let msg = result.message || 'Error al eliminar el atajo';
        if (result.details) {
          try {
            const parsed = typeof result.details === 'string' ? JSON.parse(result.details) : result.details;
            if (parsed.message) msg = parsed.message;
          } catch (_) {
            msg = result.details;
          }
        }
        throw new Error(msg);
      }

      showSuccessToast('Atajo eliminado correctamente.');
      setItemToDelete(null);
      await fetchShortcuts();
    } catch (err) {
      showErrorToast(err.message || 'Error al eliminar el atajo.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Filtrado de atajos por tipo (la búsqueda de texto la realiza directamente la BD vía vBuscar)
  const filteredShortcuts = shortcuts.filter(item => {
    if (filterType === 'global') return !item.idUsuario;
    if (filterType === 'personal') return !!item.idUsuario;
    return true;
  });

  const countGlobal = shortcuts.filter(s => !s.idUsuario).length;
  const countPersonal = shortcuts.filter(s => !!s.idUsuario).length;

  return (
    <div className="se-card-panel mb-4">
      <style>{`
        @keyframes modalDialogScaleIn {
          0% {
            opacity: 0;
            transform: scale(0.94) translateY(-14px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>

      {/* Header Sección */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h5 className="fw-bold mb-1 d-flex align-items-center gap-2" style={{ fontSize: '1.15rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
            <div
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: isDark ? 'rgba(16, 185, 129, 0.2)' : '#dcfce7',
                color: isDark ? '#34d399' : '#059669'
              }}
            >
              <FontAwesomeIcon icon={faBolt} style={{ fontSize: '0.9rem' }} />
            </div>
            <span>Atajos y Respuestas Rápidas</span>
          </h5>
          <p className="small mb-0" style={{ fontSize: '0.84rem', color: isDark ? '#94a3b8' : '#64748b' }}>
            Administra los comandos que los agentes pueden utilizar escribiendo{' '}
            <kbd
              className="border px-1"
              style={{
                backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                color: isDark ? '#38bdf8' : '#0f172a',
                borderColor: isDark ? '#233142' : '#e2e8f0'
              }}
            >
              /
            </kbd>{' '}
            en el chat de soporte.
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-success rounded-pill px-3 shadow-none d-flex align-items-center gap-2 fw-semibold"
            style={{ backgroundColor: '#059669', borderColor: '#059669' }}
            onClick={handleOpenCreate}
          >
            <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.78rem' }} />
            <span>Nuevo Atajo</span>
          </button>
        </div>
      </div>

      {/* Barra de Filtros y Búsqueda */}
      <div
        className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 pt-2 border-top"
        style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
      >
        {/* Input de búsqueda */}
        <div
          className="d-flex align-items-center gap-2 px-3 py-1 border rounded-pill"
          style={{
            width: '320px',
            maxWidth: '100%',
            backgroundColor: isDark ? '#182635' : '#f8fafc',
            borderColor: isDark ? '#233142' : '#e2e8f0'
          }}
        >
          <FontAwesomeIcon icon={faSearch} className="text-muted" style={{ fontSize: '0.82rem' }} />
          <input
            type="text"
            className="border-0 bg-transparent shadow-none w-100"
            style={{ fontSize: '0.84rem', outline: 'none', color: isDark ? '#f9fafd' : '#0f172a' }}
            placeholder="Buscar atajo por comando, título o texto..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              className="btn btn-sm p-0 text-muted border-0 bg-transparent"
              onClick={() => setSearchTerm('')}
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.75rem' }} />
            </button>
          )}
        </div>

        {/* Píldoras de Filtro */}
        <div className="d-flex align-items-center gap-1 flex-wrap">
          <button
            type="button"
            className={`btn btn-sm rounded-pill px-3 shadow-none border ${
              filterType === 'all' ? 'btn-success text-white' : ''
            }`}
            style={{
              fontSize: '0.78rem',
              fontWeight: filterType === 'all' ? 600 : 500,
              backgroundColor: filterType === 'all' ? undefined : (isDark ? '#182635' : '#f8fafc'),
              borderColor: filterType === 'all' ? undefined : (isDark ? '#233142' : '#e2e8f0'),
              color: filterType === 'all' ? undefined : (isDark ? '#94a3b8' : '#64748b')
            }}
            onClick={() => setFilterType('all')}
          >
            Todos ({shortcuts.length})
          </button>
          <button
            type="button"
            className={`btn btn-sm rounded-pill px-3 shadow-none border ${
              filterType === 'global' ? 'btn-primary text-white' : ''
            }`}
            style={{
              fontSize: '0.78rem',
              fontWeight: filterType === 'global' ? 600 : 500,
              backgroundColor: filterType === 'global' ? undefined : (isDark ? '#182635' : '#f8fafc'),
              borderColor: filterType === 'global' ? undefined : (isDark ? '#233142' : '#e2e8f0'),
              color: filterType === 'global' ? undefined : (isDark ? '#94a3b8' : '#64748b')
            }}
            onClick={() => setFilterType('global')}
          >
            <FontAwesomeIcon icon={faGlobe} className="me-1" />
            Globales ({countGlobal})
          </button>
          <button
            type="button"
            className={`btn btn-sm rounded-pill px-3 shadow-none border ${
              filterType === 'personal' ? 'btn-success text-white' : ''
            }`}
            style={{
              fontSize: '0.78rem',
              fontWeight: filterType === 'personal' ? 600 : 500,
              backgroundColor: filterType === 'personal' ? undefined : (isDark ? '#182635' : '#f8fafc'),
              borderColor: filterType === 'personal' ? undefined : (isDark ? '#233142' : '#e2e8f0'),
              color: filterType === 'personal' ? undefined : (isDark ? '#94a3b8' : '#64748b')
            }}
            onClick={() => setFilterType('personal')}
          >
            <FontAwesomeIcon icon={faUser} className="me-1" />
            Personales ({countPersonal})
          </button>
        </div>
      </div>

      {/* Lista de Atajos */}
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-success" role="status" style={{ width: '2rem', height: '2rem' }} />
          <p className="small mt-2 mb-0" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Cargando catálogo de atajos...</p>
        </div>
      ) : filteredShortcuts.length === 0 ? (
        <div
          className="text-center py-5 rounded-3 border border-dashed"
          style={{ backgroundColor: isDark ? '#121e2d' : '#f8fafc', borderColor: isDark ? '#233142' : '#cbd5e1' }}
        >
          <div
            className="mx-auto rounded-circle d-flex align-items-center justify-content-center mb-2"
            style={{ width: '48px', height: '48px', backgroundColor: isDark ? '#182635' : '#e2e8f0', color: isDark ? '#94a3b8' : '#64748b' }}
          >
            <FontAwesomeIcon icon={faBolt} style={{ fontSize: '1.2rem' }} />
          </div>
          <h6 className="fw-bold mb-1" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
            {searchTerm ? 'No se encontraron atajos con esa búsqueda' : 'No hay atajos configurados'}
          </h6>
          <p className="small mb-3" style={{ maxWidth: '380px', margin: '0 auto', color: isDark ? '#94a3b8' : '#64748b' }}>
            {searchTerm
              ? 'Intenta con otro término o limpia el buscador para ver todos los atajos.'
              : 'Comienza creando tu primer atajo rápido para agilizar las respuestas en el chat de soporte.'}
          </p>
          {!searchTerm && (
            <button
              type="button"
              className="btn btn-sm btn-success rounded-pill px-3 fw-semibold shadow-none"
              onClick={handleOpenCreate}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              Crear primer atajo
            </button>
          )}
        </div>
      ) : (
        <div
          className="table-responsive rounded-3 border shadow-xs"
          style={{ backgroundColor: isDark ? '#121e2d' : '#ffffff', borderColor: isDark ? '#233142' : '#e2e8f0' }}
        >
          <table className="table align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0, backgroundColor: isDark ? '#121e2d' : '#ffffff' }}>
            <thead>
              <tr style={{ borderBottom: isDark ? '1px solid #233142' : '1px solid #e2e8f0' }}>
                <th
                  className="py-3 px-3 fw-bold text-uppercase"
                  style={{ fontSize: '0.74rem', letterSpacing: '0.04em', color: isDark ? '#94a3b8' : '#64748b', width: '24%' }}
                >
                  TÍTULO & COMANDO
                </th>
                <th
                  className="py-3 px-3 fw-bold text-uppercase"
                  style={{ fontSize: '0.74rem', letterSpacing: '0.04em', color: isDark ? '#94a3b8' : '#64748b', width: '18%' }}
                >
                  ASIGNACIÓN
                </th>
                <th
                  className="py-3 px-3 fw-bold text-uppercase"
                  style={{ fontSize: '0.74rem', letterSpacing: '0.04em', color: isDark ? '#94a3b8' : '#64748b', width: '48%' }}
                >
                  CUERPO DEL MENSAJE
                </th>
                <th
                  className="py-3 px-3 fw-bold text-uppercase text-end"
                  style={{ fontSize: '0.74rem', letterSpacing: '0.04em', color: isDark ? '#94a3b8' : '#64748b', width: '10%' }}
                >
                  ACCIONES
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredShortcuts.map((item, idx) => {
                const command = item.vComando || item.label || '';
                const title = item.vTitulo || item.title || command;
                const text = item.vTexto || item.text || '';
                const isGlobal = !item.idUsuario;

                return (
                  <tr
                    key={item.idAtajo || command || idx}
                    style={{ borderBottom: isDark ? '1px solid #233142' : '1px solid #f1f5f9' }}
                  >
                    {/* TÍTULO & COMANDO */}
                    <td className="py-3 px-3 align-middle">
                      <div className="fw-bold mb-1" style={{ fontSize: '0.92rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                        {title}
                      </div>
                      <span
                        className="d-inline-block font-monospace px-2 py-0 rounded-2"
                        style={{
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          color: isDark ? '#34d399' : '#0ca678',
                          backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#e6fcf5',
                          border: isDark ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #c3fae8'
                        }}
                      >
                        {command.startsWith('/') ? command : `/${command}`}
                      </span>
                    </td>

                    {/* ÁMBITO / ASIGNACIÓN */}
                    <td className="py-3 px-3 align-middle">
                      {isGlobal ? (
                        <span
                          className="badge rounded-pill px-3 py-1 fw-semibold d-inline-flex align-items-center gap-1"
                          style={{
                            backgroundColor: isDark ? 'rgba(14, 165, 233, 0.15)' : '#e0f2fe',
                            color: isDark ? '#38bdf8' : '#0284c7',
                            border: isDark ? '1px solid rgba(14, 165, 233, 0.3)' : '1px solid #bae6fd',
                            fontSize: '0.74rem'
                          }}
                        >
                          <FontAwesomeIcon icon={faGlobe} style={{ fontSize: '0.72rem' }} />
                          <span>Global</span>
                        </span>
                      ) : (
                        <span
                          className="badge rounded-pill px-3 py-1 fw-semibold d-inline-flex align-items-center gap-1"
                          style={{
                            backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#e6fcf5',
                            color: isDark ? '#34d399' : '#0ca678',
                            border: isDark ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #c3fae8',
                            fontSize: '0.74rem'
                          }}
                        >
                          <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.72rem' }} />
                          <span>{item.vCreatedUser || 'Agente'}</span>
                        </span>
                      )}
                    </td>

                    {/* CUERPO DEL MENSAJE */}
                    <td className="py-3 px-3 align-middle">
                      <div
                        className="p-2 px-3 rounded-2"
                        style={{
                          backgroundColor: isDark ? '#182635' : '#f8fafc',
                          border: isDark ? '1px solid #233142' : '1px solid #e2e8f0',
                          fontSize: '0.84rem',
                          lineHeight: 1.45,
                          color: isDark ? '#cbd5e1' : '#334155',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {text}
                      </div>
                    </td>

                    {/* ACCIONES */}
                    <td className="py-3 px-3 align-middle text-end">
                      <div className="d-flex align-items-center justify-content-end gap-1">
                        <button
                          type="button"
                          className="btn btn-sm p-1 border-0 shadow-none"
                          style={{ background: 'transparent', color: isDark ? '#94a3b8' : '#64748b' }}
                          title="Editar atajo"
                          onClick={() => handleOpenEdit(item)}
                        >
                          <FontAwesomeIcon icon={faEdit} style={{ fontSize: '0.88rem' }} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm p-1 border-0 shadow-none"
                          style={{ background: 'transparent', color: isDark ? '#94a3b8' : '#64748b' }}
                          title="Eliminar atajo"
                          disabled={isDeleting}
                          onClick={() => setItemToDelete(item)}
                        >
                          <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.88rem' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL CREAR / EDITAR ATAJO                                                */}
      {/* ========================================================================= */}
      {isFormOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', zIndex: 1060 }}
          onClick={() => { if (!isSaving) setIsFormOpen(false); }}
        >
          <div
            className="card border-0 shadow-lg"
            style={{
              maxWidth: '520px',
              width: '92%',
              borderRadius: '16px',
              backgroundColor: isDark ? '#121e2d' : '#ffffff',
              border: isDark ? '1px solid #233142' : 'none',
              color: isDark ? '#f9fafd' : '#0f172a',
              animation: 'modalDialogScaleIn 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header Modal */}
            <div
              className="d-flex justify-content-between align-items-center p-3 px-4 border-bottom"
              style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
            >
              <div className="d-flex align-items-center gap-2">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: isDark ? 'rgba(16, 185, 129, 0.2)' : '#dcfce7',
                    color: isDark ? '#34d399' : '#059669'
                  }}
                >
                  <FontAwesomeIcon icon={faBolt} style={{ fontSize: '0.85rem' }} />
                </div>
                <h6 className="fw-bold mb-0" style={{ fontSize: '1.05rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                  {editingId ? 'Editar Atajo' : 'Nuevo Atajo de Soporte'}
                </h6>
              </div>
              <button
                type="button"
                className="btn btn-sm border-0 rounded-circle p-1"
                style={{
                  width: '28px',
                  height: '28px',
                  backgroundColor: isDark ? '#182635' : '#f1f5f9',
                  color: isDark ? '#94a3b8' : '#64748b'
                }}
                disabled={isSaving}
                onClick={() => setIsFormOpen(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Formulario */}
            <form onSubmit={handleSave} className="p-4">
              <div className="mb-3">
                <label className="form-label fw-bold small mb-1" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                  Comando <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span
                    className="input-group-text fw-bold font-monospace"
                    style={{
                      backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                      borderColor: isDark ? '#233142' : '#ced4da',
                      color: isDark ? '#38bdf8' : '#64748b'
                    }}
                  >
                    /
                  </span>
                  <input
                    type="text"
                    className="form-control font-monospace shadow-none"
                    placeholder="ej: saludo, retraso_envio"
                    value={formCommand}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ' || e.code === 'Space') {
                        e.preventDefault();
                        setFormCommand(prev => (prev && !prev.endsWith('_') ? `${prev}_` : prev));
                      }
                    }}
                    onChange={e => setFormCommand(e.target.value.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '').toLowerCase())}
                    required
                    disabled={isSaving}
                    style={{
                      backgroundColor: isDark ? '#182635' : '#ffffff',
                      borderColor: isDark ? '#233142' : '#ced4da',
                      color: isDark ? '#f9fafd' : '#0f172a'
                    }}
                  />
                </div>
                <small style={{ fontSize: '0.73rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                  Sin espacios. Usa Enter o Espacio para separar con guion bajo (<span className="fw-semibold">_</span>).
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold small mb-1" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                  Título descriptivo
                </label>
                <input
                  type="text"
                  className="form-control shadow-none"
                  placeholder="ej: Saludo inicial de bienvenida"
                  value={formTitle}
                  onChange={e => setFormTitle(e.target.value)}
                  disabled={isSaving}
                  style={{
                    backgroundColor: isDark ? '#182635' : '#ffffff',
                    borderColor: isDark ? '#233142' : '#ced4da',
                    color: isDark ? '#f9fafd' : '#0f172a'
                  }}
                />
                <small style={{ fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                  Nombre descriptivo para identificarlo fácilmente en el catálogo.
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold small mb-1" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                  Mensaje / Respuesta Rápida <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control shadow-none"
                  rows={4}
                  placeholder="Escribe el texto completo que se insertará en el chat..."
                  value={formText}
                  onChange={e => setFormText(e.target.value)}
                  required
                  disabled={isSaving}
                  style={{
                    fontSize: '0.88rem',
                    lineHeight: 1.4,
                    backgroundColor: isDark ? '#182635' : '#ffffff',
                    borderColor: isDark ? '#233142' : '#ced4da',
                    color: isDark ? '#f9fafd' : '#0f172a'
                  }}
                />
              </div>

              <div
                className="form-check mb-4 p-2 px-4 rounded border"
                style={{
                  backgroundColor: isDark ? '#182635' : '#f8fafc',
                  borderColor: isDark ? '#233142' : '#e2e8f0'
                }}
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="chkGlobalShortcutSettings"
                  checked={formIsGlobal}
                  onChange={e => setFormIsGlobal(e.target.checked)}
                  disabled={isSaving}
                />
                <label
                  className="form-check-label small fw-semibold"
                  htmlFor="chkGlobalShortcutSettings"
                  style={{ color: isDark ? '#f9fafd' : '#0f172a' }}
                >
                  Hacer este atajo <strong className="text-primary">global</strong> para todos los agentes de soporte
                </label>
              </div>

              {/* Botones del Modal */}
              <div
                className="d-flex justify-content-end align-items-center gap-2 pt-2 border-top"
                style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
              >
                <button
                  type="button"
                  className="btn btn-sm border rounded-pill px-3 shadow-none fw-semibold"
                  style={{
                    backgroundColor: isDark ? '#182635' : '#f1f5f9',
                    borderColor: isDark ? '#233142' : '#ced4da',
                    color: isDark ? '#cbd5e1' : '#64748b'
                  }}
                  disabled={isSaving}
                  onClick={() => setIsFormOpen(false)}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="btn btn-sm btn-success rounded-pill px-4 shadow-none fw-semibold d-flex align-items-center gap-2"
                  style={{ backgroundColor: '#059669', borderColor: '#059669' }}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <div className="spinner-border spinner-border-sm text-white" role="status" />
                      <span>Guardando...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} style={{ fontSize: '0.8rem' }} />
                      <span>{editingId ? 'Guardar Cambios' : 'Crear Atajo'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL CONFIRMAR ELIMINACIÓN                                               */}
      {/* ========================================================================= */}
      {itemToDelete && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', zIndex: 1070 }}
          onClick={() => { if (!isDeleting) setItemToDelete(null); }}
        >
          <div
            className="card border-0 shadow-lg p-4 text-center"
            style={{
              maxWidth: '410px',
              width: '92%',
              borderRadius: '16px',
              backgroundColor: isDark ? '#121e2d' : '#ffffff',
              border: isDark ? '1px solid #233142' : 'none',
              color: isDark ? '#f9fafd' : '#0f172a',
              animation: 'modalDialogScaleIn 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              className="mx-auto rounded-circle d-flex align-items-center justify-content-center mb-3"
              style={{
                width: '48px',
                height: '48px',
                backgroundColor: isDark ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2',
                color: isDark ? '#f87171' : '#dc2626'
              }}
            >
              <FontAwesomeIcon icon={faTrash} style={{ fontSize: '1.2rem' }} />
            </div>

            <h5 className="fw-bold mb-2" style={{ fontSize: '1.1rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
              ¿Eliminar atajo?
            </h5>

            <p className="small mb-4" style={{ lineHeight: 1.45, color: isDark ? '#94a3b8' : '#64748b' }}>
              ¿Estás seguro de que deseas eliminar el comando{' '}
              <strong style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>{itemToDelete.vComando || itemToDelete.label}</strong>?
              <br />
              <span style={{ fontSize: '0.75rem', opacity: 0.85 }}>Esta acción desactivará el comando para todos los agentes.</span>
            </p>

            <div className="d-flex justify-content-center gap-2">
              <button
                type="button"
                className="btn btn-sm border px-4 rounded-pill shadow-none fw-semibold"
                style={{
                  backgroundColor: isDark ? '#182635' : '#f1f5f9',
                  borderColor: isDark ? '#233142' : '#ced4da',
                  color: isDark ? '#cbd5e1' : '#64748b'
                }}
                disabled={isDeleting}
                onClick={() => setItemToDelete(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger px-4 rounded-pill shadow-none fw-semibold d-flex align-items-center gap-2"
                disabled={isDeleting}
                onClick={handleConfirmDelete}
              >
                {isDeleting ? (
                  <>
                    <div className="spinner-border spinner-border-sm text-white" role="status" />
                    <span>Eliminando...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.78rem' }} />
                    <span>Eliminar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShortcutsSettingsSection;
